import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "p",
  "br",
  "div",
  "span",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "sub",
  "sup",
  "strike",
  "ul",
  "ol",
  "li",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "caption",
  "colgroup",
  "col",
  "a",
  "img",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
  "hr"
];

const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "colspan",
  "rowspan",
  "scope",
  "border",
  "cellpadding",
  "cellspacing"
];

function isEffectivelyBold(textNode) {
  let el = textNode.parentElement;
  let explicitWeight = null;
  while (el) {
    const style = el.getAttribute("style") || "";
    const weightMatch = style.match(/font-weight\s*:\s*(normal|bold|[1-9]00)/i);
    if (weightMatch && explicitWeight === null) {
      const w = weightMatch[1].toLowerCase();
      explicitWeight = w === "bold" || Number.parseInt(w, 10) >= 500 ? "bold" : "normal";
    }
    const tag = el.tagName.toLowerCase();
    if (tag === "b" || tag === "strong") {
      return explicitWeight === null ? true : explicitWeight === "bold";
    }
    el = el.parentElement;
  }
  return explicitWeight === "bold";
}

function normalizeBoldTags(container) {
  const boldNodes = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (isEffectivelyBold(node)) {
      boldNodes.push(node);
    }
  }

  Array.from(container.querySelectorAll("b, strong"))
    .reverse()
    .forEach((el) => {
      while (el.firstChild) {
        el.parentNode.insertBefore(el.firstChild, el);
      }
      el.remove();
    });

  boldNodes.forEach((tn) => {
    if (!tn.parentNode) return;
    const strong = document.createElement("strong");
    tn.parentNode.insertBefore(strong, tn);
    strong.appendChild(tn);
  });
}

const TP_COPY_KEY = "tp-internal-copy";

export function setInternalCopyData(html, plainText) {
  try {
    localStorage.setItem(TP_COPY_KEY, JSON.stringify({ html, plainText, timestamp: Date.now() }));
  } catch {
    /* quota exceeded – ignore */
  }
}

export function getInternalCopyData() {
  try {
    const raw = localStorage.getItem(TP_COPY_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > 120000) {
      localStorage.removeItem(TP_COPY_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function clearInternalCopyData() {
  localStorage.removeItem(TP_COPY_KEY);
}

export function sanitizeInternalHtml(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR, "style"],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ["class", "id"]
  });
}

const ALLOWED_URI_PROTOCOLS = ["ftps?", "https?", "mailto", "tel", "callto", "cid", "xmpp", "blob", "data"];
const ALLOWED_URI_REGEXP = new RegExp(
  `^(?:(?:${ALLOWED_URI_PROTOCOLS.join("|")}):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))`,
  "i"
);

export function sanitizePastedHtml(html) {
  let cleaned = html
    .replaceAll(/<o:p[\s\S]*?<\/o:p>/gi, "")
    .replaceAll(/<\/?(?:xml|st1:|o:|w:|m:)[\s\S]*?>/gi, "")
    .replaceAll(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "")
    .replaceAll(/<!--[\s\S]*?-->/g, "")
    .replaceAll(/<style[\s\S]*?<\/style>/gi, "");

  const temp = document.createElement("div");
  temp.innerHTML = cleaned;
  normalizeBoldTags(temp);
  cleaned = temp.innerHTML;

  cleaned = DOMPurify.sanitize(cleaned, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ADD_DATA_URI_TAGS: ["img"],
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ["style", "class", "id"]
  });

  return cleaned;
}
